import { memo, useState } from "react";
import type { ChannelKind } from "./ChannelGlyph";
import { ChannelBadge } from "./ChannelGlyph";

interface AvatarProps {
  name?: string;
  size?: number;
  tone?: string;
}

const PALETTE = [
  "var(--primary-light)",
  "var(--info-bg)",
  "var(--soft-cloud)",
  "var(--primary-bg)",
  "var(--success-bg)",
  "var(--warning-bg)",
];

const AvatarImpl = ({ name = "?", size = 40, tone }: AvatarProps) => {
  const idx = (name.charCodeAt(0) || 0) % PALETTE.length;
  const bg = tone || PALETTE[idx];
  const initials = name
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <span
      className="so-avatar"
      style={{
        width: size,
        height: size,
        background: bg,
        color: "var(--ink)",
        fontSize: size * 0.36,
      }}
    >
      {initials}
    </span>
  );
};

export const Avatar = memo(AvatarImpl);

interface AvatarBadgeProps {
  name: string;
  channel: Exclude<ChannelKind, "all">;
  size?: number;
  /** Customer profile picture from the platform (FB/IG/LINE). Falls back to
   *  the initial-based <Avatar/> when null/empty or when the image errors. */
  avatarUrl?: string | null;
}

export function AvatarBadge({ name, channel, size = 40, avatarUrl }: AvatarBadgeProps) {
  // Provider URLs (esp. Meta's signed fbcdn/lookaside links) expire, so the
  // image can 404 at any time. Remembering which URL failed — rather than a
  // boolean, or mutating style on the DOM node — means a row recycled for a
  // different conversation, or a refreshed avatar, renders the image again.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const showImage = avatarUrl != null && avatarUrl !== "" && avatarUrl !== failedUrl;

  return (
    <span
      style={{
        position: "relative",
        display: "inline-flex",
        flex: "none",
      }}
    >
      {showImage ? (
        <img
          src={avatarUrl}
          alt=""
          loading="lazy"
          referrerPolicy="no-referrer"
          width={size}
          height={size}
          style={{
            width: size,
            height: size,
            borderRadius: "50%",
            objectFit: "cover",
            display: "block",
            background: "var(--soft-cloud)",
          }}
          onError={() => setFailedUrl(avatarUrl)}
        />
      ) : (
        <Avatar name={name} size={size} />
      )}
      <span style={{ position: "absolute", right: -1, bottom: -1 }}>
        <ChannelBadge kind={channel} size={size * 0.28} />
      </span>
    </span>
  );
}
